import { Stack } from "@mui/material";
import { FormTitleFontStyle, OrganizationRoutes } from "@simplyk/common";
import { useRouter } from "next/router";
import { UseFormReturn } from "react-hook-form";

import { isRequired } from "../../../../../common/helpers/validators";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { FormStepContainer } from "../FormStepContainer";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { FontStyleSelector } from "@/components/LiveEditorElements/FontStyleSelector/FontStyleSelector";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { containsRaffleWord } from "@/features/LiveFormEditor/common/sweepstakes";

export type StepTitleFormData = {
  title: string;
  titleFontStyle: FormTitleFontStyle;
};

export type LiveFormEditorV2StepTitleStatelessProps = {
  handleSaveAndNext: () => Promise<void>;
  form: UseFormReturn<StepTitleFormData>;
  wording: {
    stepTitle: string;
  };
  formId?: string;
  onDeleteDraftForm?: () => Promise<void>;
  furthestStepReached?: number;
  showBackButton?: boolean;
  onBackToPreviousStep?: () => void;
  showFontStyleSelector?: boolean;
  trackOnFontStyleChange?: (fontStyle: FormTitleFontStyle) => void;
  forbiddenWordMessage?: string;
};

export const LiveFormEditorV2StepTitleStateless = ({
  form,
  handleSaveAndNext,
  wording,
  formId,
  onDeleteDraftForm,
  furthestStepReached,
  showBackButton = true,
  onBackToPreviousStep,
  showFontStyleSelector = true,
  trackOnFontStyleChange,
  forbiddenWordMessage,
}: LiveFormEditorV2StepTitleStatelessProps) => {
  const { t } = useTranslate();
  const router = useRouter();
  const { isSmallScreen } = useMediaQuery();

  const { logUserInputEvent } = useLiveFormAmplitudeEvents();

  const handleBackToChoices = async () => {
    if (formId && onDeleteDraftForm && furthestStepReached === 0) {
      await onDeleteDraftForm();
    }

    await router.push(OrganizationRoutes.FormsNew);
  };

  return (
    <FormStepContainer
      title={wording.stepTitle}
      actionsProps={{
        onBack: onBackToPreviousStep ?? (showBackButton ? handleBackToChoices : undefined),
        backButtonLabel: onBackToPreviousStep
          ? undefined
          : isSmallScreen
            ? t("common", "choices")
            : t("dashboard", "common.goToFormChoice"),
        onNext: handleSaveAndNext,
        disableBack: false,
      }}
      form={form}
    >
      <Stack rowGap={3}>
        <LiveEditorBlock gap={1.5}>
          <DashboardTypography variant="subtitle1">{t("dashboard", "editor.eventTitle.label")}</DashboardTypography>
          <DashboardTextField
            name="title"
            placeholder={t("dashboard", "liveFormEditor.stepTitle.ticketing.placeholder")}
            noAsterisk
            control={form.control}
            rules={[
              isRequired(t("dashboard", "form.errors.titleRequired")),
              ...(forbiddenWordMessage
                ? [
                    {
                      validate: (value: string) => !containsRaffleWord(value) || forbiddenWordMessage,
                    },
                  ]
                : []),
            ]}
            errorMessage={form.formState.errors.title?.message}
            data-test="title-input"
            onBlur={logUserInputEvent({
              inputName: "title",
              isEmpty: !form.getValues("title").trim(),
              isValid: !form.formState.errors.title,
            })}
          />
        </LiveEditorBlock>

        {showFontStyleSelector && (
          <FontStyleSelector name="titleFontStyle" control={form.control} onFontStyleChange={trackOnFontStyleChange} />
        )}
      </Stack>
    </FormStepContainer>
  );
};
